import { Component, ChangeDetectionStrategy, Input, ChangeDetectorRef, ViewChild, AfterViewInit, Renderer2, NgZone, Output, EventEmitter, DestroyRef, inject, TemplateRef } from '@angular/core';
import { fromEvent, BehaviorSubject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { EnvironmentConfiguration } from '@nts/std/environments';
import { SlideMenu, SlideMenuModule } from 'primeng/slidemenu';
import { FilledButtonComponent } from '../../shared/buttons/filled-button/filled-button.component';
import { TelemetryService } from '@nts/std/telemetry';
import { RibbonButtonComponent } from '../../shared/buttons/ribbon-button/ribbon-button.component';
import { AsyncPipe, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NTSTranslatePipe } from '../../pipe/nts-translation-pipe';
import type { CommandsGroupInterface } from '@nts/std/interfaces';
import type { ToolBarComponentInterface, UICommandInterface } from '@nts/std/interfaces';
import { FilledButtonType } from '@nts/std/types';

@UntilDestroy()
@Component({
  selector: 'nts-tool-bar',
  templateUrl: './tool-bar.component.html',
  styleUrls: ['./tool-bar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgIf,
    RibbonButtonComponent,
    AsyncPipe,
    SlideMenuModule,
    FilledButtonComponent,
    NgFor,
    NTSTranslatePipe,
    NgTemplateOutlet
  ]
})

export class ToolBarComponent implements AfterViewInit, ToolBarComponentInterface {

  @Input() moreOptionsMenuItemList$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
  @Input() mobileMenuItemList$: BehaviorSubject<MenuItem[]> = new BehaviorSubject<MenuItem[]>([]);
  @Input() moreOptionsCommand: UICommandInterface;
  @Input() mobileMenuCommand: UICommandInterface;
  @Input() commandsGroup: Array<CommandsGroupInterface>;
  @Input() defaultCommand?: UICommandInterface;
  @Input() defaultCommandType?: FilledButtonType;
  @Input() customButtonTemplate?: TemplateRef<any>;
  @Input() customMobileButtonTemplate?: TemplateRef<any>;
  @Input() customMoreOptionButtonTemplate?: TemplateRef<any>;
  @Input() customDefaultCommandTemplate?: TemplateRef<any>;

  @Output() onMoreOptionClick = new EventEmitter<SlideMenu>();
  @Output() onMobileMenuClick = new EventEmitter<SlideMenu>();
  @Output() onToolBarReady = new EventEmitter<void>();

  @ViewChild('moreOptionsMenu', { static: false }) moreOptionsMenu: SlideMenu;
  @ViewChild('mobileMenu', { static: false }) mobileMenu: SlideMenu;

  moreOptionMenuViewportHeight$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  mobileMenuViewportHeight$: BehaviorSubject<number> = new BehaviorSubject<number>(0);
  mobileMenuViewportWidth$: BehaviorSubject<number> = new BehaviorSubject<number>(0);;
  menuDocumentClickListener: any;
  private skipNextMoreOptionsMenuReset = false;
  private skipNextMobileMenuReset = false;
  destroyRef = inject(DestroyRef);

  constructor(
    protected cd: ChangeDetectorRef,
    protected environment: EnvironmentConfiguration,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly telemetryService: TelemetryService
  ) { }

  // eslint-disable-next-line @typescript-eslint/no-misused-promises
  async ngAfterViewInit(): Promise<void> {
    await this.checkMoreOptionsMenu();
    await this.checkMobileMenu();

    this.moreOptionsMenuItemList$.pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      if (this.skipNextMoreOptionsMenuReset) {
        this.skipNextMoreOptionsMenuReset = false;
        void this.checkMoreOptionsMenu();
        return;
      }

      this.resetSlideMenuState(this.moreOptionsMenu);
      void this.checkMoreOptionsMenu();
    });

    this.mobileMenuItemList$.pipe(
      untilDestroyed(this),
    ).subscribe(() => {
      if (this.skipNextMobileMenuReset) {
        this.skipNextMobileMenuReset = false;
        void this.checkMobileMenu();
        return;
      }

      this.resetSlideMenuState(this.mobileMenu);
      void this.checkMobileMenu();
    });

    fromEvent(window, 'resize').pipe(
      untilDestroyed(this),
      debounceTime(100)
    ).subscribe(
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      async evt => {
        await this.checkMoreOptionsMenu();
        await this.checkMobileMenu();
      }
    );
    this.onToolBarReady.emit();
  }

  closeAllMenu() {
    this.resetSlideMenuState(this.moreOptionsMenu);
    this.resetSlideMenuState(this.mobileMenu);
  }

  async executeCommand(command: UICommandInterface, e: any) {
    this.closeAllMenu();

    // E' stato aggiunto un timeout di 100 ms per dare tempo ai menù di chiudersi e quindi non lasciarli aperti se si cambia pagina, #3887
    setTimeout(() => {
      command.execute(command, e);
      this.telemetryService.trackEvent({
        name: `Command ${command.displayName} executed`,
      }, { command });
    }, 100);
  }

  async moreOptionClick(moreOptionsMenu: SlideMenu, event: any, component: RibbonButtonComponent) {
    if (!moreOptionsMenu.visible) {
      this.skipNextMoreOptionsMenuReset = true;
      this.onMoreOptionClick.emit(this.moreOptionsMenu);

      await this.checkMoreOptionsMenu();
      setTimeout(() => this.bindMenuDocumentClickListener(this.moreOptionsMenu?.containerViewChild?.nativeElement, component.element.nativeElement), 250);
      this.telemetryService.trackEvent({ name: 'MoreOptions Menu Opened' });
    } else {
      this.unbindMenuDocumentClickListener();
      this.telemetryService.trackEvent({ name: 'MoreOptions Menu Closed' });
    }
    moreOptionsMenu.toggle(event);
  }

  async mobileMenuClick(mobileMenu: SlideMenu, event: any, component: RibbonButtonComponent) {
    if (this.moreOptionsMenu) {
      this.moreOptionsMenu.hide();
    }
    if (!mobileMenu.visible) {
      this.skipNextMobileMenuReset = true;
      this.onMobileMenuClick.emit(this.mobileMenu);

      await this.checkMobileMenu(this.resolveAnchorElement(event, component));
      this.telemetryService.trackEvent({ name: 'Mobile Menu Opened' });
    } else {
      this.unbindMenuDocumentClickListener();
      this.telemetryService.trackEvent({ name: 'Mobile Menu Closed' });
    }
    mobileMenu.toggle(event);

    if (!mobileMenu.visible) {
      return;
    }

    setTimeout(() => {
      this.recomputeMobileMenuViewportFromOverlay();
      this.bindMenuDocumentClickListener(
        this.mobileMenu?.containerViewChild?.nativeElement,
        component.element.nativeElement
      );
    }, 250);
  }

  trackById(index: number, command: UICommandInterface) {
    return command.uid;
  }

  onSlideMenuHide(menu?: SlideMenu) {
    this.resetSlideMenuState(menu, false);
  }

  private bindMenuDocumentClickListener(nativeElement, buttonNativeElement) {
    if (!nativeElement || !buttonNativeElement) {
      return;
    }
    this.unbindMenuDocumentClickListener();
    if (!this.menuDocumentClickListener) {
      this.zone.runOutsideAngular(() => {
        const documentTarget: any = nativeElement.ownerDocument;
        this.menuDocumentClickListener = this.renderer.listen(documentTarget, 'mousedown', (event) => {
          if (this.isOutsideClicked(event, nativeElement) && this.isOutsideClicked(event, buttonNativeElement)) {
            this.unbindMenuDocumentClickListener();
            this.zone.run(() => {
              this.cd.markForCheck();
              this.moreOptionsMenu?.hide();
              this.mobileMenu?.hide();
            });
          }

        });
      });
    }
  }

  private unbindMenuDocumentClickListener() {
    if (this.menuDocumentClickListener) {
      this.menuDocumentClickListener();
      this.menuDocumentClickListener = null;
    }
  }

  private isOutsideClicked(event: Event, nativeElement) {
    return !(nativeElement.isSameNode(event.target) || nativeElement.contains(event.target));
  }

  private async checkMoreOptionsMenu() {
    if (this.moreOptionsMenuItemList$.value?.length > 0) {
      const maxViewportHeight = window.innerHeight - 140;
      const height = 48 * (this.moreOptionsMenuItemList$?.value.length + 2);
      this.moreOptionMenuViewportHeight$.next(height > maxViewportHeight ? maxViewportHeight : height);
    } else {
      this.moreOptionMenuViewportHeight$.next(0);
    }
  }

  private async checkMobileMenu(anchorElement?: HTMLElement) {
    if (this.mobileMenuItemList$.value?.length > 0) {
      const viewportPadding = 12;
      const defaultMaxViewportHeight = window.innerHeight - 140;
      const anchorBottom = anchorElement?.getBoundingClientRect?.().bottom;

      const calculatedHeight = anchorBottom != null
        ? Math.floor(window.innerHeight - anchorBottom - viewportPadding)
        : defaultMaxViewportHeight;

      const maxViewportHeight = Math.max(1, calculatedHeight);
      this.mobileMenuViewportHeight$.next(maxViewportHeight);

      const maxViewportWidth = 560;
      const width = window.innerWidth - 15;
      this.mobileMenuViewportWidth$.next(width > maxViewportWidth ? maxViewportWidth : width);
      // this.mobileMenu.container.nativeElement.focus()
    } else {
      this.mobileMenuViewportHeight$.next(0);
      this.mobileMenuViewportWidth$.next(0);
    }
  }

  private resolveAnchorElement(event: any, component?: RibbonButtonComponent): HTMLElement | undefined {
    const componentElement = component?.element?.nativeElement as HTMLElement | undefined;
    if (componentElement) {
      return componentElement;
    }

    const eventCurrentTarget = event?.currentTarget as HTMLElement | undefined;
    if (eventCurrentTarget) {
      return eventCurrentTarget;
    }

    const eventTarget = event?.target as HTMLElement | undefined;
    return eventTarget;
  }

  private recomputeMobileMenuViewportFromOverlay() {
    const overlayElement = this.mobileMenu?.containerViewChild?.nativeElement as HTMLElement | undefined;
    if (!overlayElement) {
      return;
    }

    const viewportPadding = 12;
    const overlayTop = overlayElement.getBoundingClientRect().top;
    const availableHeight = Math.floor(window.innerHeight - overlayTop - viewportPadding);
    const finalHeight = Math.max(1, availableHeight);

    if (this.mobileMenuViewportHeight$.value !== finalHeight) {
      this.mobileMenuViewportHeight$.next(finalHeight);
      this.cd.markForCheck();
    }
  }

  private resetSlideMenuState(menu?: SlideMenu, hideMenu = true) {
    if (!menu) {
      return;
    }

    this.unbindMenuDocumentClickListener();
    this.unbindPrimeSlideMenuTransitions(menu);

    if (hideMenu) {
      menu.hide();
    }

    const slideMenu = menu as SlideMenu & {
      activeItemPath?: { set: (value: never[]) => void; };
      activeLevel?: { set: (value: number) => void; };
    };

    slideMenu.activeItemPath?.set([]);
    slideMenu.activeLevel?.set(0);
    this.cd.markForCheck();
  }

  private unbindPrimeSlideMenuTransitions(menu?: SlideMenu) {
    const slideMenu = menu as SlideMenu & {
      unbindTransitionListeners?: () => void;
    };

    slideMenu.unbindTransitionListeners?.();
  }
}
